import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { AF } from '../../api';

interface AFStoreState {
  // AF 데이터를 tokenAddress로 조회할 수 있는 맵
  afDataMap: Map<string, AF>;
  
  // 로딩 상태
  isLoading: boolean;
  
  // 에러 상태
  error: string | null;
  
  // 액션들
  setAFData: (afData: AF[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // 게터들
  getAFByAddress: (tokenAddress: string) => AF | undefined;
  getAllAFData: () => AF[];
  getAFCount: () => number;
  
  // Legendary 관련 게터들
  getLegendaryAFs: () => AF[];
  getLegendaryAddresses: () => string[];
  getLegendaryAFByAddress: (address: string) => AF | undefined;
  
  // 초기화
  reset: () => void;
}

export const useAFStore = create<AFStoreState>()(
  devtools(
    (set, get) => ({
      // 초기 상태
      afDataMap: new Map(),
      isLoading: false,
      error: null,
      
      // AF 데이터 설정 (API 응답 배열을 맵으로 변환)
      setAFData: (afData: AF[]) => {
        const afDataMap = new Map<string, AF>();
        
        // 각 AF 데이터를 tokenAddress를 키로 하는 맵에 저장
        afData.forEach(af => {
          afDataMap.set(af.address, af);
        });
        
        set({ afDataMap, error: null });
      },
      
      // 로딩 상태 설정
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
      
      // 에러 상태 설정
      setError: (error: string | null) => {
        set({ error });
      },
      
      // 특정 주소의 AF 데이터 조회
      getAFByAddress: (tokenAddress: string) => {
        return get().afDataMap.get(tokenAddress);
      },
      
      // 모든 AF 데이터 배열로 반환
      getAllAFData: () => {
        return Array.from(get().afDataMap.values());
      },
      
      // AF 데이터 개수 반환
      getAFCount: () => {
        return get().afDataMap.size;
      },
      
      // Legendary AF들만 반환
      getLegendaryAFs: () => {
        return Array.from(get().afDataMap.values()).filter(af => af.label === 'legendary');
      },
      
      // Legendary 주소들만 반환
      getLegendaryAddresses: () => {
        return Array.from(get().afDataMap.values())
          .filter(af => af.label === 'legendary')
          .map(af => af.address);
      },
      
      // 이름으로 legendary AF 찾기
      getLegendaryAFByAddress: (address: string) => {
        return Array.from(get().afDataMap.values())
          .find(af => af.label === 'legendary' && af.address === address);
      },
      
      // 스토어 초기화
      reset: () => {
        set({ afDataMap: new Map(), isLoading: false, error: null });
      },
    }),
    {
      name: 'AFStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);
